import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const EndOfYearSettings = ({ team, refreshTeamData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [resetHistory, setResetHistory] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [csvDownloadUrl, setCsvDownloadUrl] = useState(null);

  // Fetch team members, leave balances, and reset history
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get team members
        const membersResponse = await axios.get('/api/teams/members');
        const activeMembers = membersResponse.data.filter(member => !member.isPendingInvitation);
        setTeamMembers(activeMembers);
        
        // Get leave balances for all members
        const balancesPromises = activeMembers.map(member => 
          axios.get(`/api/users/${member.id}/leave-balances`)
        );
        
        const balancesResponses = await Promise.all(balancesPromises);
        
        const balancesMap = {};
        activeMembers.forEach((member, index) => {
          balancesMap[member.id] = balancesResponses[index].data;
        });
        
        setLeaveBalances(balancesMap);
        
        // Check for pending leave approvals
        const pendingResponse = await axios.get('/api/leaves?status=pending');
        setPendingApprovals(pendingResponse.data);
        
        // Get reset history
        const historyResponse = await axios.get('/api/leave-resets/history');
        setResetHistory(historyResponse.data || []);
        
        // Check if there's a CSV available for download
        try {
          const csvResponse = await axios.get('/api/leave-resets/csv-status');
          if (csvResponse.data && csvResponse.data.available) {
            setCsvDownloadUrl('/api/leave-resets/download-csv');
          }
        } catch (csvError) {
          console.error('Error checking CSV status:', csvError);
          // Don't show error to user, just don't show download button
        }
      } catch (error) {
        console.error('Error fetching end of year data:', error);
        toast.error('Failed to load end of year data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get leave type name by ID
  const getLeaveTypeName = (typeId) => {
    // Check if the type exists in the team's leave types
    const leaveType = team?.leaveTypes?.find(type => type.id === typeId);
    if (leaveType) {
      return leaveType.name;
    }
    
    // If not found in team's leave types, return a formatted version of the ID
    return 'Unknown Leave Type';
  };

  // Handle end year reset
  const handleEndYear = async () => {
    if (pendingApprovals.length > 0) {
      toast.error('Cannot perform end of year reset while leave requests are pending approval');
      return;
    }
    
    setShowConfirmation(true);
  };

  // Confirm end year reset
  const confirmEndYear = async () => {
    setProcessing(true);
    try {
      const response = await axios.post('/api/leave-resets/end-year');
      
      // Update reset history
      setResetHistory([response.data.resetRecord, ...resetHistory]);
      
      // Update CSV download URL
      setCsvDownloadUrl('/api/leave-resets/download-csv');
      
      // Reset leave balances
      const balancesMap = { ...leaveBalances };
      Object.keys(balancesMap).forEach(userId => {
        const userBalances = balancesMap[userId];
        Object.keys(userBalances).forEach(leaveTypeId => {
          userBalances[leaveTypeId].remaining = userBalances[leaveTypeId].allocated;
        });
      });
      setLeaveBalances(balancesMap);
      
      toast.success('End of year reset completed successfully');
    } catch (error) {
      console.error('Error performing end of year reset:', error);
      toast.error(error.response?.data?.message || 'Failed to perform end of year reset');
    } finally {
      setProcessing(false);
      setShowConfirmation(false);
    }
  };

  // Download CSV
  const downloadCsv = async () => {
    if (csvDownloadUrl) {
      try {
        const response = await axios.get(csvDownloadUrl, {
          responseType: 'blob' // Important for file downloads
        });
        
        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'leave_export.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast.error('Failed to download CSV report');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">End of Year Leave Reset</h2>
        
        {/* Warning message */}
        <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-700 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">
                <strong>Warning:</strong> Resetting leave balances will restore full allocations for all leave types. This action cannot be automatically undone. The system will record previous balances, and you will need to manually add any carry-over days as adjustments after the reset if needed.
              </p>
            </div>
          </div>
        </div>
        
        {/* Current Leave Balances */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Leave Balances</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Team Member
                  </th>
                  {team?.leaveTypes?.map(leaveType => (
                    <th key={leaveType.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {leaveType.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {teamMembers.map(member => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {member.firstName} {member.lastName}
                    </td>
                    {team?.leaveTypes?.map(leaveType => {
                      const balance = leaveBalances[member.id]?.[leaveType.id];
                      return (
                        <td key={`${member.id}-${leaveType.id}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {balance ? (
                            <span>
                              {balance.remaining} days <span className="text-xs text-red-500">(will be reset)</span>
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* End Year Button */}
        <div className="flex justify-end">
          {csvDownloadUrl && (
            <button
              type="button"
              onClick={downloadCsv}
              className="mr-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Download CSV Report
            </button>
          )}
          <button
            type="button"
            onClick={handleEndYear}
            disabled={processing || pendingApprovals.length > 0}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            {processing ? 'Processing...' : 'End Year'}
          </button>
        </div>
        
        {pendingApprovals.length > 0 && (
          <div className="mt-4 text-sm text-red-600">
            <p>Cannot perform end of year reset while {pendingApprovals.length} leave request(s) are pending approval.</p>
            <p>Please approve or reject all pending requests before proceeding.</p>
          </div>
        )}
      </div>
      
      {/* Reset History */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Reset History</h3>
        
        {resetHistory.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No reset history available.</p>
        ) : (
          <div className="space-y-4">
            {resetHistory.map(reset => (
              <div key={reset.id} className="border border-gray-200 dark:border-gray-700 rounded-md">
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex items-center justify-between cursor-pointer" 
                     onClick={() => {
                       const element = document.getElementById(`reset-details-${reset.id}`);
                       if (element) {
                         element.classList.toggle('hidden');
                       }
                     }}>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Reset on {formatDate(reset.createdAt)}
                    </span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      by {reset.adminName}
                    </span>
                  </div>
                  <svg className="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
                
                <div id={`reset-details-${reset.id}`} className="hidden px-4 py-3">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Team Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Leave Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Previous Balance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Adjustment
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800">
                      {/* Group details by user */}
                      {(() => {
                        // Group details by userName
                        const groupedDetails = {};
                        reset.details.forEach(detail => {
                          if (!groupedDetails[detail.userName]) {
                            groupedDetails[detail.userName] = [];
                          }
                          groupedDetails[detail.userName].push(detail);
                        });
                        
                        // Render grouped details
                        return Object.entries(groupedDetails).map(([userName, userDetails], groupIndex) => (
                          <React.Fragment key={`group-${reset.id}-${groupIndex}`}>
                            {/* User group header */}
                            <tr className="bg-gray-100 dark:bg-gray-700">
                              <td 
                                colSpan="4" 
                                className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                {userName}
                              </td>
                            </tr>
                            {/* User's leave type details */}
                            {userDetails.map((detail, detailIndex) => (
                              <tr 
                                key={`detail-${reset.id}-${groupIndex}-${detailIndex}`}
                                className={detailIndex === userDetails.length - 1 ? "border-b-2 border-gray-300 dark:border-gray-600" : ""}
                              >
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {/* Leave empty as user name is in the group header */}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {getLeaveTypeName(detail.leaveTypeId)}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {detail.previousBalance} days
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  <span className={detail.adjustment > 0 ? "text-green-600 dark:text-green-400 font-medium" : "text-red-600 dark:text-red-400 font-medium"}>
                                    {detail.adjustment > 0 ? '+' : ''}{detail.adjustment} days
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      Confirm End of Year Reset
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to perform the end of year reset? This action will:
                      </p>
                      <ul className="list-disc list-inside mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <li>Reset all leave balances to their full allocation</li>
                        <li>Delete all calendar entries for every user</li>
                        <li>Generate a CSV report of all leave data</li>
                        <li>Create a permanent record of previous balances</li>
                      </ul>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <strong>This action cannot be undone automatically.</strong> You will need to manually add any carry-over days as adjustments after the reset.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirmEndYear}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : 'Confirm Reset'}
                </button>
                <button 
                  type="button" 
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowConfirmation(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EndOfYearSettings;
